import { useState, useEffect } from "react";
import axios from "axios";
import ThemeChip from "./ThemeChip";
import { SectionHeader } from "./SectionHeader";
import { useFilters } from "../context/FilterContext";
import { buildDashboardParams } from "../utils/dashboardFilters";
import { fmtCrNoSymbol } from "../utils/format";

/**
 * Source of Fund × Portfolio Heatmap
 * (Moved from Command Centre to Financial page.)
 *
 * Fetches:
 *   - /executive/trust-theme-matrix  → grants count & sanctioned (Cr)
 *
 * Toggle: Grants  /  ₹ Sanctioned
 *   (AWP toggle removed per stakeholder request — Feb 2026)
 */
export default function TrustThemeHeatmap() {
  const { filters, viewMode, API } = useFilters();
  const params = buildDashboardParams(filters, { view_mode: viewMode });
  const [trustMatrix, setTrustMatrix] = useState({ data: [], trusts: [], themes: [] });
  const [toggle, setToggle] = useState("alloc");

  useEffect(() => {
    axios.get(`${API}/executive/trust-theme-matrix`, { params })
      .then(r => setTrustMatrix(r.data))
      .catch(() => setTrustMatrix({ data: [], trusts: [], themes: [] }));
  }, [API, JSON.stringify(params)]);  // eslint-disable-line react-hooks/exhaustive-deps

  const matrix = trustMatrix;
  if (!matrix.trusts.length && !matrix.data.length) return null;

  return (
    <SectionHeader
      title="Source of Fund × Portfolio"
      subtitle="Grants and sanctioned amount by trust & theme"
      collapsible
      defaultOpen={true}
      headerRight={
        <div className="flex items-center gap-2">
          <button onClick={() => setToggle("grants")} data-testid="heatmap-toggle-grants"
            className={`text-[10px] px-2.5 py-1 rounded-full border font-medium transition-colors ${toggle === "grants" ? "bg-[#B71C1C] text-white border-[#B71C1C]" : "bg-white text-slate-600 border-slate-200 hover:border-[#B71C1C]"}`}>
            Grants
          </button>
          <button onClick={() => setToggle("alloc")} data-testid="heatmap-toggle-alloc"
            className={`text-[10px] px-2.5 py-1 rounded-full border font-medium transition-colors ${toggle === "alloc" ? "bg-[#B71C1C] text-white border-[#B71C1C]" : "bg-white text-slate-600 border-slate-200 hover:border-[#B71C1C]"}`}>
            ₹ Sanctioned
          </button>
        </div>
      }
    >
      <div className="overflow-x-auto" data-testid="trust-theme-heatmap-section">
        <HeatmapTable matrix={matrix} toggle={toggle} />
      </div>
    </SectionHeader>
  );
}

/* ── Sub-component (originally in CommandCentre.jsx) ────────────────── */
function HeatmapTable({ matrix, toggle }) {
  if (!matrix.data.length) return null;
  const suffix = toggle === "grants" ? "_grants" : "_alloc";
  const isCount = toggle === "grants";
  const allVals = matrix.data.flatMap(row =>
    matrix.trusts.map(t => row[`${t}${suffix}`])
  ).filter(Boolean);
  const maxVal = Math.max(...allVals, 1);
  const colTotals = matrix.trusts.map(t =>
    matrix.data.reduce((sum, row) => sum + (row[`${t}${suffix}`] || 0), 0)
  );
  const grandTotal = colTotals.reduce((a, b) => a + b, 0);
  const fmt = v => v > 0 ? (isCount ? v : fmtCrNoSymbol(v)) : "—";

  return (
    <>
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr>
            <th className="text-left text-slate-500 font-medium py-2 pr-3 w-32">Portfolio</th>
            {matrix.trusts.map(t => (
              <th key={t} className="text-center text-slate-600 font-semibold py-2 px-1.5 min-w-[64px]">{t}</th>
            ))}
            <th className="text-center text-slate-700 font-bold py-2 px-1.5 min-w-[64px] border-l border-slate-200">Total</th>
          </tr>
        </thead>
        <tbody>
          {matrix.data.map((row, ri) => {
            const rowTotal = matrix.trusts.reduce((sum, t) => sum + (row[`${t}${suffix}`] || 0), 0);
            return (
              <tr key={ri}>
                <td className="py-1.5 pr-3 font-medium text-slate-700 whitespace-nowrap text-[11px]">
                  <ThemeChip theme={row.theme} />
                </td>
                {matrix.trusts.map(t => {
                  const val = row[`${t}${suffix}`];
                  const intensity = val > 0 ? Math.max(0.08, val / maxVal) : 0;
                  const display = val > 0 ? (isCount ? val : fmtCrNoSymbol(val)) : "—";
                  return (
                    <td key={t} className="py-1.5 px-1.5 text-center rounded transition-colors"
                      style={{
                        backgroundColor: val > 0 ? `rgba(183,28,28,${intensity})` : "transparent",
                        color: intensity > 0.55 ? "#fff" : intensity > 0 ? "#B71C1C" : "#CBD5E1",
                      }}>
                      <span className="font-semibold text-[11px]">{display}</span>
                    </td>
                  );
                })}
                <td className="py-1.5 px-1.5 text-center border-l border-slate-200 bg-slate-50">
                  <span className="font-bold text-slate-700 text-[11px]">{fmt(rowTotal)}</span>
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-slate-200">
            <td className="py-2 pr-3 font-bold text-slate-700 text-[11px]">Total</td>
            {colTotals.map((total, i) => (
              <td key={i} className="py-2 px-1.5 text-center bg-slate-50">
                <span className="font-bold text-slate-700 text-[11px]">{fmt(total)}</span>
              </td>
            ))}
            <td className="py-2 px-1.5 text-center border-l border-slate-200 bg-slate-100">
              <span className="font-bold text-[#B71C1C] text-[11px]">{fmt(grandTotal)}</span>
            </td>
          </tr>
        </tfoot>
      </table>
      <div className="flex items-center gap-3 mt-3 text-[10px] text-slate-600">
        <span>Intensity:</span>
        {[0.1, 0.3, 0.6, 1].map(o => (
          <span key={o} className="flex items-center gap-1">
            <span className="w-4 h-3 rounded-sm inline-block" style={{ backgroundColor: `rgba(183,28,28,${o})` }} />
            {o === 0.1 ? "Low" : o === 0.3 ? "Med" : o === 0.6 ? "High" : "Max"}
          </span>
        ))}
      </div>
    </>
  );
}
